import { IScore, IRunOff, IEventSummary } from "@eagerdog/interfaces";
import { Constants } from "@eagerdog/constants";

import { getShowSpecificData, getResultType } from "./result-data";
import { fastCatPreviewMetrics } from "./helpers/fast-cat";

/** A single labelled stat to surface for a score (e.g. "Score" → "197"). */
export interface ScoreSummaryField {
  label: string;
  value: string;
}

export interface ScoreSummaryContext {
  sanctioningClub?: string;
  /** All scores for the show — required to compute placements. */
  allScores?: IScore[];
  runOffs?: IRunOff[];
}

/**
 * Ordered [label, key] pairs per show type. `key` indexes into the object
 * returned by `getShowSpecificData`, so the mobile list and the web Results
 * grid surface the exact same computed values from one source of truth.
 */
type FieldSpec = Array<[string, string]>;

const FIELD_SPECS: Record<string, FieldSpec> = {
  [Constants.show_type.obedience]: [
    ["Position", "placement"],
    ["Score", "score"],
    ["Deductions", "totalDeductions"],
  ],
  [Constants.show_type.rally_obedience]: [
    ["Score", "score"],
    ["Deductions", "totalDeductions"],
    ["Time", "time"],
  ],
  [Constants.show_type.scentwork]: [
    ["Position", "placement"],
    ["Search Time", "searchTime"],
    ["Faults", "faults"],
    ["Finds", "numFinds"],
    ["Result Reason", "resultReason"],
  ],
  [Constants.show_type.nosework]: [
    ["Section", "section"],
    ["Search Time", "searchTime"],
    ["Result Reason", "resultReason"],
    ["Excluded from Placement", "excludedFromPlacement"],
  ],
  [Constants.show_type.herding]: [
    ["Section", "section"],
    ["Position", "placement"],
    ["Score", "score"],
    ["Time", "elapsedTime"],
  ],
  [Constants.show_type.pre_trial_herding]: [
    ["Section", "section"],
    ["Position", "placement"],
    ["Score", "score"],
    ["Time", "elapsedTime"],
  ],
  [Constants.show_type.instinct_test_herding]: [
    ["Section", "section"],
    ["Position", "placement"],
    ["Score", "score"],
    ["Time", "elapsedTime"],
  ],
  [Constants.show_type.fast_cat]: [
    ["Time Slot", "timeSlot"],
    ["Time", "time"],
  ],
  [Constants.show_type.urban_rat_race]: [
    ["Section", "section"],
    ["Time", "time"],
    ["Faults", "faultCount"],
    ["Final", "finalTime"],
    ["Rats", "ratsFound"],
  ],
  [Constants.show_type.country_rat_race]: [
    ["Section", "section"],
    ["Time", "time"],
    ["Faults", "faultCount"],
    ["Final", "finalTime"],
    ["Rats", "ratsFound"],
  ],
  [Constants.show_type.lost_and_found]: [
    ["Section", "section"],
    ["Time", "time"],
    ["Faults", "faultCount"],
    ["Final", "finalTime"],
  ],
  [Constants.show_type.weight_pull]: [
    ["Class", "weightClass"],
    ["Dog Weight", "dogWeight"],
    ["Highest Weight", "highestWeight"],
    ["% Pulled", "percentPulled"],
    ["Time", "time"],
  ],
  [Constants.show_type.water_dog]: [["Time", "time"]],
  [Constants.show_type.precision_coursing]: [["Search Time", "searchTime"]],
  [Constants.show_type.shed_dog]: [["Search Time", "searchTime"]],
  [Constants.show_type.scent]: [
    ["FEO", "feo"],
    ["Scents", "scents"],
    ["Fault 1", "fault1"],
    ["Fault 2", "fault2"],
    ["Search Time", "searchTime"],
  ],
  [Constants.show_type.cwags_games]: [["FEO", "feo"]],
  [Constants.show_type.scent_dog_games]: [
    ["Section", "section"],
    ["Position", "placement"],
    ["Faults", "faultCount"],
    ["Time", "searchTime"],
    ["Eff. Time", "effectiveTime"],
  ],
  [Constants.show_type.barn_hunt]: [
    ["Section", "section"],
    ["Division", "heightDivision"],
    ["Position", "placement"],
    ["Score", "score"],
    ["Time", "time"],
  ],
  [Constants.show_type.obedience_show_n_go]: [["Time Slot", "timeSlot"]],
  [Constants.show_type.general_rally]: [["Time Slot", "timeSlot"]],
  [Constants.show_type.farm_dog]: [["Judge", "judge"]],
};

const AGILITY_AKC_SPEC: FieldSpec = [
  ["Position", "placement"],
  ["Score", "score"],
  ["Time", "timeToCompete"],
  ["Course", "courseTime"],
  ["Faults", "totalFaults"],
  ["Jump Height", "jumpHeight"],
];

const AGILITY_UKC_SPEC: FieldSpec = [
  ["Score", "score"],
  ["Time", "time"],
  ["Faults", "faults"],
  ["Time Faults", "timeFaults"],
  ["Excluded from Placement", "excludedFromPlacement"],
];

const toField = (
  label: string,
  value: unknown
): ScoreSummaryField | undefined => {
  if (value === undefined || value === null || value === false) return undefined;
  if (value === true) return { label, value: "Yes" };
  const str = typeof value === "number" ? String(value) : String(value);
  if (str === "" || str === "N/A") return undefined;
  return { label, value: str };
};

const withClubFields = (
  showType: string,
  sanctioningClub: string | undefined,
  spec: FieldSpec
): FieldSpec => {
  const next = [...spec];
  const isCwags = sanctioningClub === Constants.sanctioning_club.C_WAGS;
  const isAkc = sanctioningClub === Constants.sanctioning_club.AKC;
  const isFourH = sanctioningClub === Constants.sanctioning_club.FH;
  if (
    isCwags &&
    (showType === Constants.show_type.obedience ||
      showType === Constants.show_type.rally_obedience)
  ) {
    next.unshift(["Section", "section"]);
  }
  if (
    isAkc &&
    (showType === Constants.show_type.obedience ||
      showType === Constants.show_type.rally_obedience)
  ) {
    next.push(["Jump Height", "jumpHeight"]);
  }
  if (isFourH && showType === Constants.show_type.agility) {
    next.unshift(["Section", "section"]);
  }
  return next;
};

const isNotQualifiedResult = (resultType: string) =>
  resultType === Constants.score_result_type.not_qualified ||
  resultType === "nq" ||
  resultType === "NQ";

const mapSpec = (
  data: Record<string, any>,
  spec: FieldSpec,
  resultType: string
): ScoreSummaryField[] => {
  const out: ScoreSummaryField[] = [];
  for (const [label, key] of spec) {
    if (key === "searchTime" && isNotQualifiedResult(resultType)) {
      out.push({ label, value: "N/A" });
      continue;
    }
    const field = toField(label, data[key]);
    if (field) out.push(field);
  }
  return out;
};

/**
 * Returns the ordered, show-type-specific stats to display for a score
 * (e.g. Position / Score / Total Deductions / Faults / Search Time).
 *
 * Values are sourced from `getShowSpecificData` — the same function the web
 * Results grid uses — so both platforms stay in lock-step. Empty / N/A values
 * and entries with no result entered are omitted (no noise on pending rows).
 */
export const getScoreSummaryFields = (
  row: IScore,
  ctx: ScoreSummaryContext = {}
): ScoreSummaryField[] => {
  const { sanctioningClub, allScores, runOffs } = ctx;

  // Fast Dash has no web column today; surface its time as a mobile convenience.
  if (row.show_type === Constants.show_type.fast_dash) {
    const field = toField("Time", row.fastdash?.time);
    return field ? [field] : [];
  }

  // No stats for un-scored, scratched, or conflicted entries (status badge covers those).
  const resultType = getResultType(row);
  if (
    !resultType ||
    resultType === "na" ||
    resultType === Constants.score_result_type.scratched ||
    resultType === Constants.score_result_type.conflict
  ) {
    return [];
  }

  // Cat notes live outside getShowSpecificData (web shows them in a Notes column).
  if (row.show_type === Constants.show_type.cat) {
    const notes = (row.cat as (typeof row.cat & { notes?: string }) | undefined)
      ?.notes;
    const field = toField("Notes", notes);
    return field ? [field] : [];
  }

  const event = { sanctioning_club: sanctioningClub } as IEventSummary;
  const data = getShowSpecificData(row.show_type, row, allScores, event, runOffs);

  // Dock jumping has separate feet/inches — combine into one distance chip.
  if (row.show_type === Constants.show_type.dock_jumping) {
    const { feet, inches } = data;
    if (feet !== undefined || inches !== undefined) {
      return [{ label: "Distance", value: `${feet ?? 0}' ${inches ?? 0}"` }];
    }
    return [];
  }

  let spec = FIELD_SPECS[row.show_type];
  if (row.show_type === Constants.show_type.agility) {
    spec =
      sanctioningClub === Constants.sanctioning_club.AKC
        ? AGILITY_AKC_SPEC
        : AGILITY_UKC_SPEC;
  }
  if (!spec) return [];

  const fields = mapSpec(data, withClubFields(row.show_type, sanctioningClub, spec), resultType);

  if (
    row.show_type === Constants.show_type.fast_cat &&
    sanctioningClub === Constants.sanctioning_club.AKC &&
    !isNotQualifiedResult(resultType)
  ) {
    const metrics = fastCatPreviewMetrics(row.fastcat?.time, row.height_at_withers);
    if (metrics) {
      fields.push(
        { label: "Handicap", value: String(metrics.handicap_multiplier) },
        { label: "Points", value: metrics.points_earned.toFixed(2) }
      );
    }
  }

  return fields;
};
